// ThermalDesignContext.tsx
import React, { createContext, useContext, useState, useCallback, useMemo } from "react";
import { ThermalDesignContextType, NodeSpecItem, NodeUnits, EnvironmentalParameterItem, MaterialsData, OpenModal, NodeValues, CoatingPreset, ThermalFaceId } from "./ThermalDesignTypes";
import type { SubsystemInstance } from "./SatelliteDesigner/ThermalDesigner/types/subsystems";
import type { ThermalModelConfig } from "./SatelliteDesigner/ThermalDesigner/types/ThermalConfig";
import { createAllDefaultSubsystemInstances } from "./SatelliteDesigner/ThermalDesigner/types/subsystems";

// Thermal face constants (moved from thermalFaces.ts)
export const THERMAL_FACE_ORDER: ThermalFaceId[] = ["+X", "-X", "+Y", "-Y", "+Z", "-Z"];

export const THERMAL_FACE_LABEL: Record<ThermalFaceId, string> = {
  "+X": "BOTTOM FACE (+X)",
  "-X": "TOP FACE (-X)",
  "+Y": "RIGHT FACE (+Y)",
  "-Y": "LEFT FACE (-Y)",
  "+Z": "FRONT FACE (+Z)",
  "-Z": "BACK FACE (-Z)",
};

export const THERMAL_FACE_BODY_VEC: Record<ThermalFaceId, [number, number, number]> = {
  "+X": [1, 0, 0],
  "-X": [-1, 0, 0],
  "+Y": [0, 1, 0],
  "-Y": [0, -1, 0],
  "+Z": [0, 0, 1],
  "-Z": [0, 0, -1],
};

/**
 * Utility to get face ID from a body vector.
 * Returns the matching face ID or null if no match found.
 */
export const getFaceIdFromBodyVec = (bodyVec?: number[] | null): ThermalFaceId | null => {
  if (!bodyVec || bodyVec.length !== 3) return null;
  const key = bodyVec.join(",");
  return THERMAL_FACE_ORDER.find((faceId) => THERMAL_FACE_BODY_VEC[faceId].join(",") === key) ?? null;
};

// Maximum number of nodes allowed in the thermal model
export const MAX_NODE_COUNT = 9;

// Number of face items maintained in nodeSpecsOptions (6 external + 2 internal decks)
export const DEFAULT_NODE_COUNT = 8;

// ============ CONSTANTS ============ //

export const DEFAULT_NODE_VALUES: NodeValues = {
  node_surface_area: 0,
  node_mass: 0,
  node_specific_heat: 0,
  node_absorptivity: 0,
  node_emissivity: 0,
  node_initial_temperature: 0,
  node_body_vec: [0, 0, 0],
  node_isInternal: false,
  node_internal_power: 0,
  node_material: "",
  node_conductance: Array(DEFAULT_NODE_COUNT).fill(0),
  node_internal_distances: undefined,
};

export const MATERIALS_DATA: MaterialsData[] = [
  {
    material_name: "Custom",
    density: 0,
    youngs_modulus: 0,
    yield_strength: 0,
    cte: 0,
    specific_heat: 0,
    thermal_conductivity: 0,
    absorptivity: 0,
    emissivity: 0,
  },
  {
    material_name: "Aluminum 6061-T6",
    density: 2700,
    youngs_modulus: 68.9,
    yield_strength: 276,
    cte: 23.6,
    specific_heat: 896,
    thermal_conductivity: 167,
    absorptivity: 0.25,
    emissivity: 0.04,
  },
  {
    material_name: "Aluminum 7075-T6",
    density: 2810,
    youngs_modulus: 71.7,
    yield_strength: 503,
    cte: 23.5,
    specific_heat: 960,
    thermal_conductivity: 130,
    absorptivity: 0.25,
    emissivity: 0.04,
  },
  {
    material_name: "Ti-6Al-4V",
    density: 4430,
    youngs_modulus: 113.8,
    yield_strength: 880,
    cte: 8.6,
    specific_heat: 560,
    thermal_conductivity: 6.7,
    absorptivity: 0.6,
    emissivity: 0.1,
  },
  {
    material_name: "Toray M40J (UD CFRP)",
    density: 1800,
    youngs_modulus: 377,
    yield_strength: 2300,
    cte: 0,
    specific_heat: 1050,
    thermal_conductivity: 5.5,
    absorptivity: 0.85,
    emissivity: 0.9,
  },
  {
    material_name: "Super Invar",
    density: 8100,
    youngs_modulus: 141,
    yield_strength: 275,
    cte: 0.3,
    specific_heat: 500,
    thermal_conductivity: 11,
    absorptivity: 0.3,
    emissivity: 0.2,
  },
  {
    material_name: "PEEK",
    density: 1320,
    youngs_modulus: 3.6,
    yield_strength: 90,
    cte: 47,
    specific_heat: 1260,
    thermal_conductivity: 0.25,
    absorptivity: 0.6,
    emissivity: 0.8,
  },
  {
    material_name: "ClearCeram-Z",
    density: 2520,
    youngs_modulus: 90,
    yield_strength: 0,
    cte: 0.0,
    specific_heat: 800,
    thermal_conductivity: 2.5,
    absorptivity: 0.3,
    emissivity: 0.8,
  },
  {
    material_name: "POM (Delrin)",
    density: 1410,
    youngs_modulus: 2.9,
    yield_strength: 70,
    cte: 110,
    specific_heat: 1470,
    thermal_conductivity: 0.37,
    absorptivity: 0.7,
    emissivity: 0.85,
  },
  {
    material_name: "AA2195 Aluminum",
    density: 2840,
    youngs_modulus: 79,
    yield_strength: 450,
    cte: 18.7,
    specific_heat: 880,
    thermal_conductivity: 150,
    absorptivity: 0.25,
    emissivity: 0.04,
  },
  {
    material_name: "Honeycomb Sandwich",
    density: 1250,
    youngs_modulus: 70,
    yield_strength: 0,
    cte: 1,
    specific_heat: 1000,
    thermal_conductivity: 3.2,
    absorptivity: 0.6,
    emissivity: 0.8,
  },
];

export const NODE_SPECS_UNITS: NodeUnits = {
  node_surface_area: "m²",
  node_mass: "kg",
  node_specific_heat: "J/g·K",
  node_absorptivity: "",
  node_emissivity: "",
  node_initial_temperature: "°C",
  node_body_vec: ["X", "Y", "Z"],
  node_internal_power: "W",
  node_material: "",
  node_material_properties: {
    specific_heat: "J/kg·K",
    thermal_conductivity: "W/m·K",
    density: "kg/m³",
    absorptivity: "",
    emissivity: "",
    youngs_modulus: "GPa",
    yield_strength: "MPa",
    cte: "10⁻⁶/K",
  },
};

export const DEFAULT_ENVIRONMENTAL_PARAMETERS: EnvironmentalParameterItem[] = [
  { key: "albedo_factor", name: "Albedo of Earth", isEnabled: true, value: 0.306, unit: "" },
  { key: "blackbody_temperature", name: "Blackbody Temperature", isEnabled: true, value: 255, unit: "K" },
  { key: "solar_constant", name: "Solar Constant", isEnabled: true, value: 1361, unit: "W/m²" },
];

export const DEFAULT_COATING_CONFIG = {
  none: { percentage: 0 },
  mli: { percentage: 0, absorptivity: 0, emissivity: 0, area_density: 0, specific_heat: 0, thermal_conductance: 0, preset_id: null },
  osr: { percentage: 0, absorptivity: 0, emissivity: 0, area_density: 0, specific_heat: 0, thermal_conductance: 0, preset_id: null },
};

export const COATING_PRESETS: CoatingPreset[] = [
  {
    id: "OSR (Rigid)",
    label: "OSR - Rigid",
    absorptivity: 0.08,
    emissivity: 0.78,
    areaDensity: 3.5,
    specificHeat: 800,
    thermalConductance: 5.0,
  },
  {
    id: "OSR (Flexible)",
    label: "OSR - Flexible",
    absorptivity: 0.08,
    emissivity: 0.78,
    areaDensity: 0.7,
    specificHeat: 1100,
    thermalConductance: 3.0,
  },
  {
    id: "MLI (5)",
    label: "MLI - 5 Layers",
    absorptivity: 0.15,
    emissivity: 0.06,
    areaDensity: 0.22,
    specificHeat: 1582.2,
    thermalConductance: 0.16,
  },
  {
    id: "MLI (10)",
    label: "MLI - 10 Layers",
    absorptivity: 0.15,
    emissivity: 0.03,
    areaDensity: 0.37,
    specificHeat: 1582.2,
    thermalConductance: 0.07,
  },
  {
    id: "MLI (15)",
    label: "MLI - 15 Layers",
    absorptivity: 0.15,
    emissivity: 0.02,
    areaDensity: 0.56,
    specificHeat: 1582.2,
    thermalConductance: 0.03,
  },
];

const DEFAULT_MODAL_STATE: OpenModal = {
  isNodeSpecsModalOpen: false,
  isNodeSpecsJsonModalOpen: false,
  isThermalConductivityModalOpen: false,
  isThermalConductivityJsonModalOpen: false,
  isCustomMaterialModalOpen: false,
  isCoatingSettingsModalOpen: false,
  isConfigureCoatingModalOpen: false,
  isSubSystemSettingsModalOpen: false,
  isSurfaceThresholdsModalOpen: false,
  activeCoatingType: null,
  activeBrush: null,
  activeSubsystemId: null,
};

/**
 * Generates initial node specs array with specified count
 */
const generateNodeSpecs = (count: number = DEFAULT_NODE_COUNT): NodeSpecItem[] => {
  // New model: default is 6 external faces + 2 internal decks
  if (count === DEFAULT_NODE_COUNT) {
    const externalFaceNodes: NodeSpecItem[] = THERMAL_FACE_ORDER.map((faceId, index) => ({
      name: THERMAL_FACE_LABEL[faceId],
      id: String(index + 1),
      status: "unconfigured",
      isSelected: index === 0,
      nodes_per_face: 1,
      values: {
        ...DEFAULT_NODE_VALUES,
        node_isInternal: false,
        node_body_vec: [...THERMAL_FACE_BODY_VEC[faceId]],
        node_internal_distances: undefined,
        node_conductance: Array(DEFAULT_NODE_COUNT).fill(0),
      },
    }));

    const deck1Node: NodeSpecItem = {
      name: "Deck 1",
      id: "INT1",
      status: "unconfigured",
      isSelected: false,
      nodes_per_face: 1,
      values: {
        ...DEFAULT_NODE_VALUES,
        node_isInternal: true,
        node_specific_heat: 896,
        node_body_vec: [0, 0, 0],
        node_internal_distances: undefined,
        node_conductance: Array(DEFAULT_NODE_COUNT).fill(0),
      },
    };

    const deck2Node: NodeSpecItem = {
      name: "Deck 2",
      id: "INT2",
      status: "unconfigured",
      isSelected: false,
      nodes_per_face: 1,
      values: {
        ...DEFAULT_NODE_VALUES,
        node_isInternal: true,
        node_specific_heat: 896,
        node_body_vec: [0, 0, 0],
        node_internal_distances: undefined,
        node_conductance: Array(DEFAULT_NODE_COUNT).fill(0),
      },
    };

    return [...externalFaceNodes, deck1Node, deck2Node];
  }

  // Fallback: legacy behavior for other counts
  return Array.from({ length: count }, (_, index) => ({
    name: `Node ${String(index + 1).padStart(2, "0")}`,
    id: String(index + 1),
    status: "unconfigured",
    isSelected: index === 0,
    nodes_per_face: 1,
    values: { ...DEFAULT_NODE_VALUES, node_conductance: Array(count).fill(0) },
  }));
};

// ============ CONTEXT ============

const ThermalDesignContext = createContext<ThermalDesignContextType | undefined>(undefined);

export const ThermalDesignProvider = ({ children }: { children: React.ReactNode }) => {
  const nodeSpecsInitialArray = generateNodeSpecs(DEFAULT_NODE_COUNT);

  const [nodeSpecsOptions, setNodeSpecsOptions] = useState<NodeSpecItem[]>(nodeSpecsInitialArray);
  const [count, setCount] = useState<number>(nodeSpecsInitialArray.length);
  const [environmentalParametersOptions, setEnvironmentalParametersOptions] =
    useState<EnvironmentalParameterItem[]>(DEFAULT_ENVIRONMENTAL_PARAMETERS);
  const [materialsData, setMaterialsData] = useState<MaterialsData[]>(MATERIALS_DATA);
  const [isSatDesignerSidebarOpen, setIsSatDesignerSidebarOpen] = useState<boolean>(true);
  const [isOpenModal, setIsOpenModal] = useState<OpenModal>(DEFAULT_MODAL_STATE);
  const [showNodes, setShowNodes] = useState<boolean>(false);
  const [showAxisLabels, setShowAxisLabels] = useState<boolean>(true);
  const [showNadirLabel, setShowNadirLabel] = useState<boolean>(false);
  const [showOrientationGizmo, setShowOrientationGizmo] = useState<boolean>(true);
  const [showCoatingView, setShowCoatingView] = useState<boolean>(false);

  // Subsystem state
  const [subsystemInstances, setSubsystemInstances] = useState<SubsystemInstance[]>(() => createAllDefaultSubsystemInstances());
  const [showSubsystems, setShowSubsystems] = useState<boolean>(true);
  const [showSubsystemView, setShowSubsystemView] = useState<boolean>(false);
  const [thermalModel, setThermalModel] = useState<ThermalModelConfig | undefined>(undefined);

  // Subsystem management methods
  const addSubsystemInstance = useCallback((instance: SubsystemInstance) => {
    setSubsystemInstances(prev => [...prev, instance]);
  }, []);

  const removeSubsystemInstance = useCallback((id: string) => {
    setSubsystemInstances(prev => prev.filter(inst => inst.id !== id));
  }, []);

  const updateSubsystemInstance = useCallback((id: string, updates: Partial<SubsystemInstance>) => {
    setSubsystemInstances(prev =>
      prev.map(inst => (inst.id === id ? { ...inst, ...updates } : inst))
    );
  }, []);
  const value: ThermalDesignContextType = useMemo(() => ({
    count,
    setCount,
    nodeSpecsOptions,
    setNodeSpecsOptions,
    environmentalParametersOptions,
    setEnvironmentalParametersOptions,
    nodeSpecsUnits: NODE_SPECS_UNITS,
    isSatDesignerSidebarOpen,
    setIsSatDesignerSidebarOpen,
    openModal: isOpenModal,
    setOpenModal: setIsOpenModal,
    materialsData,
    setMaterialsData,
    showNodes,
    setShowNodes,
    showAxisLabels,
    setShowAxisLabels,
    showNadirLabel,
    setShowNadirLabel,
    showOrientationGizmo,
    setShowOrientationGizmo,
    showCoatingView,
    setShowCoatingView,
    subsystemInstances,
    setSubsystemInstances,
    showSubsystems,
    setShowSubsystems,
    addSubsystemInstance,
    removeSubsystemInstance,
    updateSubsystemInstance,
    showSubsystemView,
    setShowSubsystemView,
    thermalModel,
    setThermalModel,
  }), [count, setCount, nodeSpecsOptions, setNodeSpecsOptions, environmentalParametersOptions, setEnvironmentalParametersOptions, isSatDesignerSidebarOpen, setIsSatDesignerSidebarOpen, isOpenModal, setIsOpenModal, materialsData, setMaterialsData, showNodes, setShowNodes, showAxisLabels, setShowAxisLabels, showNadirLabel, setShowNadirLabel, showOrientationGizmo, setShowOrientationGizmo, showCoatingView, setShowCoatingView, subsystemInstances, setSubsystemInstances, showSubsystems, setShowSubsystems, addSubsystemInstance, removeSubsystemInstance, updateSubsystemInstance, showSubsystemView, setShowSubsystemView, thermalModel, setThermalModel]);

  return <ThermalDesignContext.Provider value={value}>{children}</ThermalDesignContext.Provider>;
};

export const useThermalDesign = () => {
  const context = useContext(ThermalDesignContext);
  if (!context) {
    throw new Error("useThermalDesign must be used inside a ThermalDesignProvider");
  }
  return context;
};

interface SubsystemConfigContextValue {
  showSubsystemsView: boolean;
  selectedSubsystemId: string | null;
  setShowSubsystemsView: (show: boolean) => void;
  setSelectedSubsystemId: (instanceId: string | null) => void;
}

const SubsystemConfigContext = createContext<SubsystemConfigContextValue | undefined>(undefined);

export const SubsystemConfigProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [showSubsystemsView, setShowSubsystemsView] = useState(false);
  const [selectedSubsystemId, setSelectedSubsystemId] = useState<string | null>(null);

  const value: SubsystemConfigContextValue = {
    showSubsystemsView,
    selectedSubsystemId,
    setShowSubsystemsView,
    setSelectedSubsystemId,
  };

  return (
    <SubsystemConfigContext.Provider value={value}>
      {children}
    </SubsystemConfigContext.Provider>
  );
};

export const useSubsystemConfig = (): SubsystemConfigContextValue => {
  const context = useContext(SubsystemConfigContext);
  if (!context) {
    throw new Error("useSubsystemConfig must be used within a SubsystemConfigProvider");
  }
  return context;
};

export const ThermalDesignerProviders: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  return (
    <ThermalDesignProvider>
      <SubsystemConfigProvider>
        {children}
      </SubsystemConfigProvider>
    </ThermalDesignProvider>
  );
};